import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Search, Eye, Calendar, Download, XCircle, Trash2, User, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import BulkActionBar from '../../components/BulkActionBar';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ReservationsAdminPage = () => {
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [selectedIds, setSelectedIds] = useState([]);
  const limit = 20;

  useEffect(() => {
    fetchReservations();
  }, [skip, statusFilter]);

  const fetchReservations = async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    try {
      const params = new URLSearchParams({ skip, limit });
      if (search) params.append('search', search);
      if (statusFilter) params.append('status', statusFilter);

      const response = await axios.get(`${API}/admin/reservations?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setReservations(response.data.reservations);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to load reservations');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setSkip(0);
    fetchReservations();
  };

  // Bulk selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(reservations.map(r => r.reservation?.id).filter(Boolean));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (reservationId) => {
    if (selectedIds.includes(reservationId)) {
      setSelectedIds(selectedIds.filter(id => id !== reservationId));
    } else {
      setSelectedIds([...selectedIds, reservationId]);
    }
  };

  const handleClearSelection = () => {
    setSelectedIds([]);
  };

  // Bulk action execution
  const handleBulkAction = async (action, reason) => {
    const token = localStorage.getItem('token');
    
    try {
      const response = await axios.post(
        `${API}/admin/reservations/bulk-action`,
        { ids: selectedIds, action, reason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const result = response.data;
      
      if (result.success_count > 0) {
        toast.success(`Successfully ${action}ed ${result.success_count} reservation(s)`);
        fetchReservations(); // Refresh list
      }
      
      if (result.failed_count > 0) {
        toast.error(`Failed to ${action} ${result.failed_count} reservation(s)`);
      }
      
      return result;
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} reservations`);
      throw error;
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100 text-gray-800'}`}>
        {status ? status.toUpperCase() : 'N/A'}
      </span>
    );
  };

  const getInitiatorBadge = (initiatedBy) => {
    if (initiatedBy === 'RM') {
      return (
        <Badge className="bg-purple-100 text-purple-800 hover:bg-purple-100">
          <User className="w-3 h-3 mr-1" />
          RM
        </Badge>
      );
    }
    if (initiatedBy === 'PM') {
      return (
        <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">
          <Briefcase className="w-3 h-3 mr-1" />
          PM
        </Badge>
      );
    }
    return <span className="text-gray-400 text-xs">N/A</span>;
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold" style={{ color: '#1B3240' }}>Reservations Management</h1>
        <p className="text-gray-600 mt-2">View and manage all resource reservations</p>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-6">
          <div className="flex items-center space-x-4">
            <div className="flex-1 flex items-center space-x-2">
              <Input
                placeholder="Search by resource, project, or company..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button onClick={handleSearch} style={{ backgroundColor: '#62D84E' }}>
                <Search className="w-4 h-4 mr-2" />
                Search
              </Button>
            </div>
            
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setSkip(0);
              }}
              className="border rounded-md px-3 py-2"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="accepted">Accepted</option>
              <option value="rejected">Rejected</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <Button variant="outline">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Reservations Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: '#62D84E' }}></div>
            </div>
          ) : reservations.length === 0 ? (
            <div className="text-center py-12 text-gray-500">No reservations found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedIds.length === reservations.length && reservations.length > 0}
                        onChange={handleSelectAll}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Initiated By</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Resource</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">RM Company</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">PM Company</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Start Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {reservations.map((item, index) => {
                    const reservation = item.reservation || {};
                    const resource = item.resource;
                    const project = item.project;
                    const from_company = item.from_company;
                    const to_company = item.to_company;
                    const initiatedBy = item.initiated_by;
                    
                    // Determine RM and PM companies based on who initiated
                    const rmCompany = initiatedBy === 'RM' ? from_company : to_company;
                    const pmCompany = initiatedBy === 'PM' ? from_company : to_company;
                    
                    return (
                    <tr key={reservation.id || index} className="hover:bg-gray-50">
                      <td className="px-4 py-4">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(reservation.id)}
                          onChange={() => handleSelectOne(reservation.id)}
                          className="w-4 h-4 rounded border-gray-300"
                          disabled={!reservation.id}
                        />
                      </td>
                      <td className="px-4 py-4 text-sm font-mono text-gray-600">
                        {reservation.id ? reservation.id.substring(0, 8) : 'N/A'}
                      </td>
                      <td className="px-4 py-4">
                        {getInitiatorBadge(initiatedBy)}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                          <span className="text-sm font-medium text-gray-900">
                            {resource?.name || resource?.first_name && resource?.last_name 
                              ? `${resource.first_name} ${resource.last_name}` 
                              : 'N/A'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600 max-w-[150px] truncate">
                        {project?.title || 'N/A'}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {rmCompany?.name || from_company?.name || 'N/A'}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {pmCompany?.name || to_company?.name || 'N/A'}
                      </td>
                      <td className="px-4 py-4">{getStatusBadge(reservation.status)}</td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {reservation.start_date 
                          ? new Date(reservation.start_date).toLocaleDateString() 
                          : reservation.proposed_start_date 
                            ? new Date(reservation.proposed_start_date).toLocaleDateString()
                            : 'N/A'}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-600">
                        {reservation.created_at ? new Date(reservation.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-4 py-4 text-right">
                        <Link to={`/admin/reservations/${reservation.id}`}>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={!reservation.id}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  )})}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="mt-6 flex items-center justify-between">
        <p className="text-sm text-gray-600">
          Showing {skip + 1} to {Math.min(skip + limit, total)} of {total} reservations
        </p>
        <div className="space-x-2">
          <Button
            variant="outline"
            onClick={() => setSkip(Math.max(0, skip - limit))}
            disabled={skip === 0}
          >
            Previous
          </Button>
          <Button
            variant="outline"
            onClick={() => setSkip(skip + limit)}
            disabled={skip + limit >= total}
          >
            Next
          </Button>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      <BulkActionBar
        selectedIds={selectedIds}
        onClearSelection={handleClearSelection}
        actions={[
          { label: 'Cancel', value: 'cancel', color: 'text-orange-600 border-orange-600', icon: XCircle },
          { label: 'Delete', value: 'delete', color: 'text-red-600 border-red-600', icon: Trash2 }
        ]}
        onExecute={handleBulkAction}
        entityType="reservations"
      />
    </div>
  );
};

export default ReservationsAdminPage;
